import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import profileAPI from '../api/profile';
import jobsAPI from '../api/jobs';
import CompanyAutocomplete from '../components/common/CompanyAutocomplete';

const WORK_AREAS = ['צפון', 'שרון', 'מרכז', 'ירושלים', 'דרום', 'שפלה'];

const getApplicantSkillName = (applicantSkill) => (
  applicantSkill.skill?.name || applicantSkill.skill_name || applicantSkill.name || ''
);

const normalizeApplicantSkill = (applicantSkill) => ({
  id: applicantSkill.id,
  skill_id: applicantSkill.skill?.id || applicantSkill.skill_id,
  name: getApplicantSkillName(applicantSkill),
  applicant_comment: applicantSkill.applicant_comment || '',
  updated_at: applicantSkill.updated_at
});

const formatValue = (value, formatter) => (
  formatter ? formatter(value) : value
);

const isFieldFilled = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && String(value).trim() !== '';
};

const parseWorkAreas = (value) => (
  Array.isArray(value) ? value : String(value || '').split(',').map((item) => item.trim()).filter(Boolean)
);

const PencilButton = ({ fieldName, onEdit, label = 'עריכה' }) => (
  <button
    type="button"
    onClick={() => onEdit(fieldName)}
    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500"
    aria-label={label}
    title={label}
  >
    <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.862 3.487a2.1 2.1 0 112.97 2.97L8.25 18.04 4 19l.96-4.25L16.862 3.487z" />
    </svg>
  </button>
);

const ReadOnlyField = ({ fieldName, label, value, formatter, onEdit }) => {
  const displayValue = formatValue(value, formatter);

  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <div className="mt-1 flex min-h-[40px] items-start justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
        <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">{displayValue}</p>
        <PencilButton fieldName={fieldName} onEdit={onEdit} />
      </div>
    </div>
  );
};

const EditableField = ({ fieldName, label, value, children, formatter, isEditable, onEdit, onBlur }) => {
  const hasValue = isFieldFilled(value);
  if (hasValue && !isEditable) {
    return (
      <ReadOnlyField
        fieldName={fieldName}
        label={label}
        value={value}
        formatter={formatter}
        onEdit={onEdit}
      />
    );
  }

  return (
    <div
      onFocusCapture={() => onEdit(fieldName)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onBlur(fieldName);
      }}
    >
      <label htmlFor={fieldName} className="form-label">{label}</label>
      {children}
    </div>
  );
};

const Profile = () => {
  const { register, reset, setValue, watch } = useForm();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState(null);
  const [currentCompany, setCurrentCompany] = useState('');
  const [cvInfo, setCvInfo] = useState({ exists: false, updatedAt: null });
  const [hasJobTitle, setHasJobTitle] = useState(false);
  const [editableFields, setEditableFields] = useState({});
  const [skills, setSkills] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [selectedSkillId, setSelectedSkillId] = useState('');
  const [skillSearchTerm, setSkillSearchTerm] = useState('');
  const [showSkillResults, setShowSkillResults] = useState(false);
  const [isSavingSkill, setIsSavingSkill] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const skillAutocompleteRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const successTimeoutRef = useRef(null);
  const pendingProfileRef = useRef(null);
  const lastSavedProfileRef = useRef('');
  
  // Watch form changes including fields that react-hook-form owns.
  const formValues = watch();
  
  const getGenderLabel = (gender) => {
    if (gender === 'male') return 'זכר';
    if (gender === 'female') return 'נקבה';
    return '';
  };

  const getGenderedText = (maleText, femaleText) => (formValues.gender === 'female' ? femaleText : maleText);
  const selectedWorkAreas = parseWorkAreas(formValues.preferred_work_area);

  const setFieldEditable = (fieldName) => {
    setEditableFields((current) => ({
      ...current,
      [fieldName]: true
    }));
  };

  const setFieldReadOnly = (fieldName) => {
    setEditableFields((current) => ({ ...current, [fieldName]: false }));
  };

  const cvUpdatedAt = cvInfo.updatedAt ? new Date(cvInfo.updatedAt) : null;
  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
  const cvNeedsUpdate = cvUpdatedAt && cvUpdatedAt < twoMonthsAgo;

  // Profile strength: registration details are a flat 20% baseline; each of the
  // four content factors adds another 20% (updated CV, job title, last workplace, skills).
  const STRENGTH_WEIGHT = 20;
  const strengthFactors = [
    true,                             // registration details (baseline)
    cvInfo.exists && !cvNeedsUpdate,  // up-to-date CV
    hasJobTitle,                      // job title record exists
    isFieldFilled(currentCompany),   // last workplace
    skills.length > 0                 // skills
  ];
  const profileCompletion = strengthFactors.filter(Boolean).length * STRENGTH_WEIGHT;

  const selectableSkills = availableSkills.filter((skill) => (
    !skills.some((applicantSkill) => applicantSkill.skill_id === skill.id)
  ));

  const filteredSkills = selectableSkills
    .filter((skill) => (
      skillSearchTerm.trim() === '' ||
      String(skill.name || '').toLowerCase().includes(skillSearchTerm.trim().toLowerCase())
    ))
    .slice(0, 25);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (skillAutocompleteRef.current && !skillAutocompleteRef.current.contains(event.target)) {
        setShowSkillResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsFetching(true);
        setError('');

        let applicantId = currentUser?.applicant_id || currentUser?.resource?.id;
        let userData = currentUser;

        if (!applicantId) {
          const userResponse = await profileAPI.getUserData();
          userData = userResponse.data;
          applicantId = userResponse.data.resource?.id;
        }

        if (!applicantId) {
          setError('לא הצלחנו לזהות את פרופיל המועמד. נסי להתחבר מחדש.');
          return;
        }
        
        const [profileResponse, applicantSkillsResponse, skillsResponse, recentCVResponse, jobTitlesResponse] = await Promise.all([
          profileAPI.getProfile(applicantId),
          profileAPI.getApplicantSkills(),
          profileAPI.getSkills(),
          profileAPI.checkRecentCV(),
          jobsAPI.getApplicantJobTitles().catch(() => ({ data: [] }))
        ]);

        const response = profileResponse;
        
        // If user data is not included in the response, fetch it separately
        if (!response.data.user && userData) {
          try {
            response.data.user = userData.resource ? userData : (await profileAPI.getUserData()).data;
          } catch (userErr) {
            console.error('Failed to fetch user data:', userErr);
            // Use currentUser as fallback
            response.data.user = userData;
          }
        }
        
        const normalizedSkills = (applicantSkillsResponse.data || [])
          .map(normalizeApplicantSkill)
          .filter((skill) => skill.id && skill.skill_id && skill.name)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at) || b.id - a.id);

        setProfile(response.data);
        setSkills(normalizedSkills);
        setAvailableSkills(skillsResponse.data || []);
        setCvInfo({
          exists: Boolean(recentCVResponse.data?.latest_cv_at) || recentCVResponse.data?.has_recent_cv === true,
          updatedAt: recentCVResponse.data?.latest_cv_at || null
        });
        setHasJobTitle((jobTitlesResponse.data || []).length > 0);
        
        // Set form default values
        const initialValues = {
          gender: response.data.gender || '',
          about_me: response.data.about_me || '',
          current_company: response.data.current_company || '',
          preferred_work_area: response.data.preferred_work_area || '',
          salary_expectations_comment: response.data.salary_expectations_comment || response.data.max_salary_expectations || '',
          is_discrete: response.data.is_discrete || false,
          free_submission: response.data.free_submission || false
        };
        reset(initialValues);
        lastSavedProfileRef.current = JSON.stringify(initialValues);
        
        // Set current company state
        setCurrentCompany(response.data.current_company || '');
      } catch (err) {
        if (err.response?.data?.error) {
          setError(err.response.data.error);
        } else {
          setError('שגיאה בטעינת פרופיל. אנא נסה שנית.');
        }
        console.error(err);
      } finally {
        setIsFetching(false);
      }
    };
    
    if (currentUser) {
      fetchProfile();
    }
  }, [currentUser, reset]);
  
  // Handle company change from autocomplete
  const handleCompanyChange = (companyName) => {
    setCurrentCompany(companyName);
    setValue('current_company', companyName, { shouldDirty: true });
    scheduleProfileSave();
  };

  const handleWorkAreaToggle = (workArea) => {
    const nextWorkAreas = selectedWorkAreas.includes(workArea)
      ? selectedWorkAreas.filter((item) => item !== workArea)
      : [...selectedWorkAreas, workArea];
    setValue('preferred_work_area', nextWorkAreas.join(', '), { shouldDirty: true });
    scheduleProfileSave();
  };

  const addSkill = async () => {
    const selectedSkill = availableSkills.find((skill) => (
      String(skill.id) === String(selectedSkillId)
    ));

    if (!selectedSkill) {
      setError(getGenderedText('בחר skill מתוך הרשימה.', 'בחרי skill מתוך הרשימה.'));
      return;
    }

    if (skills.some((skill) => skill.skill_id === selectedSkill.id)) {
      setSelectedSkillId('');
      setSkillSearchTerm('');
      return;
    }

    try {
      setIsSavingSkill(true);
      setError('');

      const response = await profileAPI.addApplicantSkill(selectedSkill.id);
      const nextSkill = normalizeApplicantSkill(response.data);
      setSkills((currentSkills) => [nextSkill, ...currentSkills]);
      setSelectedSkillId('');
      setSkillSearchTerm('');
      setShowSkillResults(false);
    } catch (err) {
      console.error('Error adding applicant skill:', err);
      setError('שגיאה בהוספת skill. אנא נסה שנית.');
    } finally {
      setIsSavingSkill(false);
    }
  };

  const selectSkill = (skill) => {
    setSelectedSkillId(skill.id);
    setSkillSearchTerm(skill.name);
    setShowSkillResults(false);
    setError('');
  };

  const handleSkillSearchChange = (event) => {
    setSkillSearchTerm(event.target.value);
    setSelectedSkillId('');
    setShowSkillResults(true);
  };

  const removeSkill = async (skillToRemove) => {
    try {
      setIsSavingSkill(true);
      setError('');

      await profileAPI.removeApplicantSkill(skillToRemove.id);
      setSkills((currentSkills) => currentSkills.filter((skill) => skill.id !== skillToRemove.id));
    } catch (err) {
      console.error('Error removing applicant skill:', err);
      setError('שגיאה בהסרת skill. אנא נסה שנית.');
    } finally {
      setIsSavingSkill(false);
    }
  };

  const updateSkillComment = (skillId, applicantComment) => {
    setSkills((currentSkills) => currentSkills.map((skill) => (
      skill.id === skillId ? { ...skill, applicant_comment: applicantComment } : skill
    )));
  };

  const saveSkillComment = async (skill) => {
    try {
      setIsSavingSkill(true);
      setError('');
      await profileAPI.updateApplicantSkill(skill.id, {
        applicant_comment: skill.applicant_comment,
        responded_at: new Date().toISOString()
      });
      setSuccess('נשמר אוטומטית');
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Error updating applicant skill:', err);
      setError('שגיאה בשמירת פירוט הskill. אנא נסה שנית.');
    } finally {
      setIsSavingSkill(false);
    }
  };

  const saveProfile = async (data) => {
    const serializedData = JSON.stringify(data);
    if (!profile || serializedData === lastSavedProfileRef.current) return;

    try {
      setIsLoading(true);
      setError('');
      await profileAPI.updateProfile(currentUser.applicant_id || currentUser.resource?.id || profile.id, data);
      lastSavedProfileRef.current = serializedData;
      setProfile(current => ({ ...current, ...data }));
      setSuccess('נשמר אוטומטית');
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('לא הצלחנו לשמור את העדכון. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const scheduleProfileSave = () => {
    pendingProfileRef.current = watch();
    clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      saveProfile(pendingProfileRef.current);
      pendingProfileRef.current = null;
    }, 700);
  };

  const flushProfileSave = () => {
    if (!pendingProfileRef.current) return;
    clearTimeout(autoSaveTimeoutRef.current);
    saveProfile(pendingProfileRef.current);
    pendingProfileRef.current = null;
  };

  const deleteAccount = async () => {
    if (!window.confirm('מחיקת החשבון תמחק לצמיתות את הפרופיל, קורות החיים והמועמדויות שלך. לא ניתן לבטל פעולה זו. להמשיך?')) return;

    try {
      setIsDeletingAccount(true);
      setError('');
      const applicantId = currentUser?.applicant_id || currentUser?.resource?.id || profile?.id;
      await profileAPI.deleteAccount(applicantId);
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('מחיקת החשבון נכשלה. החשבון והמידע שלך לא נמחקו; אפשר לנסות שוב.');
      setIsDeletingAccount(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
        <h1 className="mb-4 text-xl font-bold text-gray-900 sm:mb-6 sm:text-2xl">פרופיל אישי</h1>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {/* Success Message */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}
        
        {isFetching ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="card">
            <div className="mb-6 rounded-md border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">חוזק הפרופיל</h2>
                  <p className="text-sm text-gray-600 mt-1">{profileCompletion}% מהפרטים מלאים</p>
                </div>
                <div className={`text-2xl font-bold ${profileCompletion >= 80 ? 'text-green-700' : 'text-yellow-700'}`}>{profileCompletion}%</div>
              </div>
              <div className="mt-4 h-3 overflow-hidden rounded-full bg-white border border-gray-200">
                <div
                  className={`h-full rounded-full transition-all ${profileCompletion >= 80 ? 'bg-green-500' : 'bg-yellow-500'}`}
                  style={{ width: `${profileCompletion}%` }}
                  aria-hidden="true"
                ></div>
              </div>
            </div>

            {profile && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">פרטים אישיים</h2>
                
                <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6">
                  <div>
                    <p className="text-sm text-gray-500">שם פרטי</p>
                    <p className="font-medium">{profile.user?.first_name || '-'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">שם משפחה</p>
                    <p className="font-medium">{profile.user?.last_name || '-'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">אימייל</p>
                    <p className="font-medium">{profile.user?.email || '-'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">טלפון</p>
                    <p className="font-medium">{profile.user?.phone_number || '-'}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={(event) => event.preventDefault()} onChange={scheduleProfileSave} onBlur={flushProfileSave}>
              <h2 className="text-lg font-semibold mb-4">עדכון פרטים</h2>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <input
                      type="checkbox"
                      {...register('is_discrete')}
                      className="mt-1 h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-800">חיפוש דיסקרטי</span>
                      <span className="block text-xs text-gray-500 mt-1">לא לפנות למעסיק נוכחי ללא אישור.</span>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <input
                      type="checkbox"
                      {...register('free_submission')}
                      className="mt-1 h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-800">{getGenderedText('אני מאשר הגשה חופשית למשרות.', 'אני מאשרת הגשה חופשית למשרות.')}</span>
                  </label>
                </div>

                <EditableField
                  fieldName="gender"
                  label="מגדר"
                  value={formValues.gender}
                  formatter={getGenderLabel}
                  isEditable={editableFields.gender}
                  onEdit={setFieldEditable}
                  onBlur={setFieldReadOnly}
                >
                  <select
                    id="gender"
                    {...register('gender')}
                    className="form-input"
                  >
                    <option value="">בחר מגדר</option>
                    <option value="male">זכר</option>
                    <option value="female">נקבה</option>
                  </select>
                </EditableField>
                
                <EditableField
                  fieldName="about_me"
                  label="על עצמי - מקצועי"
                  value={formValues.about_me}
                  isEditable={editableFields.about_me}
                  onEdit={setFieldEditable}
                  onBlur={setFieldReadOnly}
                >
                  <textarea
                    id="about_me"
                    {...register('about_me')}
                    rows={4}
                    className="form-input"
                    placeholder={getGenderedText(
                      'ספר לנו על עצמך ועל הניסיון המקצועי שלך בהרחבה (בדומה לקורות החיים שלך, אבל בשפה חופשית) - כדאי להרחיב על מקומות העבודה, השגים ותחומי התמחות',
                      'ספרי לנו על עצמך ועל הניסיון המקצועי שלך בהרחבה (בדומה לקורות החיים שלך, אבל בשפה חופשית) - כדאי להרחיב על מקומות העבודה, השגים ותחומי התמחות'
                    )}
                  ></textarea>
                </EditableField>
                
                <EditableField
                  fieldName="current_company"
                  label="מעסיק נוכחי"
                  value={currentCompany}
                  isEditable={editableFields.current_company}
                  onEdit={setFieldEditable}
                  onBlur={setFieldReadOnly}
                >
                  <CompanyAutocomplete
                    value={currentCompany}
                    onChange={handleCompanyChange}
                    placeholder={getGenderedText('שם המעסיק שבו אתה עובד כיום', 'שם המעסיק שבו את עובדת כיום')}
                    className="form-input"
                    disabled={isLoading}
                  />
                </EditableField>

                <EditableField
                  fieldName="salary_expectations_comment"
                  label="שכר מבוקש (אפשר גם טווח)"
                  value={formValues.salary_expectations_comment}
                  isEditable={editableFields.salary_expectations_comment}
                  onEdit={setFieldEditable}
                  onBlur={setFieldReadOnly}
                >
                  <input
                    id="salary_expectations_comment"
                    type="text"
                    inputMode="numeric"
                    {...register('salary_expectations_comment')}
                    className="form-input"
                    placeholder="לדוגמה 24000"
                  />
                </EditableField>

                <EditableField
                  fieldName="preferred_work_area"
                  label="אזור עבודה מועדף"
                  value={formValues.preferred_work_area}
                  isEditable={editableFields.preferred_work_area}
                  onEdit={setFieldEditable}
                  onBlur={setFieldReadOnly}
                >
                  <div id="preferred_work_area" className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                    {WORK_AREAS.map((workArea) => (
                      <label key={workArea} className="flex items-center gap-2 text-sm text-gray-800">
                        <input
                          type="checkbox"
                          checked={selectedWorkAreas.includes(workArea)}
                          onChange={() => handleWorkAreaToggle(workArea)}
                          className="h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                        />
                        <span>{workArea}</span>
                      </label>
                    ))}
                  </div>
                </EditableField>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label htmlFor="selectedSkillId" className="form-label mb-0">Skills</label>
                    {skills.length > 0 && !editableFields.skills && (
                      <PencilButton fieldName="skills" onEdit={setFieldEditable} />
                    )}
                    {skills.length > 0 && editableFields.skills && (
                      <button
                        type="button"
                        onClick={() => setEditableFields((current) => ({ ...current, skills: false }))}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        aria-label="סגירת Skills"
                        title="סגירה"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {skills.length > 0 && !editableFields.skills ? (
                    <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      {skills.map((skill) => (
                        <div key={skill.id}>
                          <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-brown-700">{skill.name}</span>
                          {skill.applicant_comment && <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{skill.applicant_comment}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <div ref={skillAutocompleteRef} className="relative flex-1">
                          <input
                            id="selectedSkillId"
                            type="text"
                            value={skillSearchTerm}
                            onChange={handleSkillSearchChange}
                            onFocus={() => setShowSkillResults(true)}
                            className="form-input"
                            placeholder={getGenderedText('הקלד כדי לחפש skill', 'הקלידי כדי לחפש skill')}
                            disabled={isSavingSkill}
                            autoComplete="off"
                          />
                          {showSkillResults && (
                            <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg">
                              {filteredSkills.length > 0 ? (
                                filteredSkills.map((skill) => (
                                  <button
                                    key={skill.id}
                                    type="button"
                                    onClick={() => selectSkill(skill)}
                                    className="block w-full px-4 py-2 text-right text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                                  >
                                    {skill.name}
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-2 text-sm text-gray-500">
                                  לא נמצאו skillים בקטלוג
                                </div>
                              )}
                            </div>
                          )}
                          {skillSearchTerm && !selectedSkillId && (
                            <p className="mt-1 text-xs text-gray-500">יש לבחור skill מהרשימה.</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={addSkill}
                          disabled={isSavingSkill || !selectedSkillId}
                          className={`btn-secondary shrink-0 ${isSavingSkill ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                          {isSavingSkill ? 'מוסיף...' : 'הוספת skill'}
                        </button>
                      </div>
                      {skills.length > 0 && (
                        <div className="space-y-3">
                          {skills.map((skill) => (
                            <div key={skill.id} className="rounded-md border border-gray-200 p-3">
                              <div className="mb-2 flex items-center justify-between gap-3">
                                <span className="font-medium text-gray-800">{skill.name}</span>
                                <button
                                  type="button"
                                  onClick={() => removeSkill(skill)}
                                  disabled={isSavingSkill}
                                  className="text-gray-500 hover:text-red-700 focus:outline-none disabled:opacity-50"
                                  aria-label={`הסרת ${skill.name}`}
                                >
                                  ×
                                </button>
                              </div>
                              <textarea
                                value={skill.applicant_comment}
                                onChange={(event) => updateSkillComment(skill.id, event.target.value)}
                                onBlur={() => saveSkillComment(skill)}
                                rows={2}
                                className="form-input"
                                placeholder={getGenderedText('פרט על הניסיון שלך בתחום', 'פרטי על הניסיון שלך בתחום')}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-md border border-gray-200 bg-white p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">קורות חיים</h3>
                      {cvInfo.exists ? (
                        <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                          <svg className="h-4 w-4 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                          </svg>
                          <strong>
                            {cvUpdatedAt ? `עודכן לאחרונה בתאריך ${cvUpdatedAt.toLocaleDateString('he-IL')}` : 'קיים קובץ קורות חיים במערכת'}
                            {cvNeedsUpdate && ' (רצוי לעדכן)'}
                          </strong>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">לא נמצא קובץ קורות חיים. אפשר להעלות קובץ כחלק מעדכון הפרטים.</p>
                      )}
                    </div>
                    <Link to="/upload-cv" state={{ fromMenu: true, returnTo: '/profile' }} className="btn-secondary">
                      העלאת קורות חיים
                    </Link>
                  </div>
                </div>
              </div>
            </form>

              <button
                type="button"
                onClick={deleteAccount}
                disabled={isDeletingAccount}
                className="mt-4 rounded-md border border-red-600 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isDeletingAccount ? 'מוחק...' : 'מחיקת החשבון שלי'}
              </button>
            </div>
        )}
      </div>
    </Layout>
  );
};

export default Profile;
